"use client";
import * as React from "react";
import Image from "next/image";
import { cva, type VariantProps } from "class-variance-authority";
import MondelezLogoLogin from "@icons/logo/mondelez/Mondelēz Logo-login-white.svg";
import GroupLogoLogin from "@icons/logo/group-AIDA/Group 4001-login-white.svg";
import MondelezLogoDashboard from "@icons/logo/mondelez/Mondelēz Logo-dashboard-purple.svg";
import GroupLogoDashboard from "@icons/logo/group-AIDA/Group 4001-dashboard-purple.svg";
import Link from "next/link";
import { AIDA_LOGO, GTM_EVENT_PAGE_NAMES, MDLZ_LOGO } from "@/constants/constants";
import { useAppContext } from "@/app/context/AppProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useOnceEffect } from "@/hooks/useOnceEffect";
import { setCurrentPageName } from "@/app/context/AppProvider/actions";
import { getAdminOnboardingStatus } from "@/lib/utils";
import { useKeycloakSession } from "@/hooks/useKeycloakSession";
import { Session } from "next-auth";

const logoVariants = cva("flex items-center w-full", {
  variants: {
    variant: {
      light: "justify-center",
      dark: "justify-start cursor-pointer w-[fit-content]",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

const dividerVariants = cva("w-[2px] h-[30px] border-r rounded-[10px] mx-[15px]", {
  variants: {
    variant: {
      light: "border-white border-opacity-40 bg-transparent",
      dark: "border-white bg-mondelez-primary-purple opacity-40",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

interface LogoSrc {
  group: string;
  mondelez: string;
}

export interface LogoProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof logoVariants> {}

const MondelezLogo = React.forwardRef<HTMLDivElement, LogoProps>(({ className, variant = "light", ...props }, ref) => {
  const { getDataAttribute } = useAnalytics();
  const { dispatch } = useAppContext();
  const { session } = useKeycloakSession();
  const { onboardingCompleated } = getAdminOnboardingStatus(session as Session);

  useOnceEffect(() => {
    dispatch(setCurrentPageName(GTM_EVENT_PAGE_NAMES.HOME));
  });

  const getLogoSrc = (variant: "light" | "dark"): LogoSrc => {
    return variant === "dark"
      ? {
          group: GroupLogoDashboard,
          mondelez: MondelezLogoDashboard,
        }
      : {
          group: GroupLogoLogin,
          mondelez: MondelezLogoLogin,
        };
  };

  const { group, mondelez } = getLogoSrc(variant ?? "light");

  const logoContent = (
    <div className="flex items-center">
      <Image alt={AIDA_LOGO} src={group} />
      <div className={dividerVariants({ variant })} />
      <Image alt={MDLZ_LOGO} src={mondelez} />
    </div>
  );
  const { state } = useAppContext();
  const { isFormUnsaved } = state ?? {};
  const dataAttributes = { ...getDataAttribute(AIDA_LOGO) };

  // Handle navigation prevention for admin onboarding
  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!onboardingCompleated) {
      e.preventDefault();
    }
  };

  return (
    <div className={logoVariants({ variant, className })} ref={ref} {...(isFormUnsaved && { "aria-haspopup": "dialog" })} {...props}>
      {variant === "dark" ? (
        <Link {...dataAttributes} href="/" onClick={handleLogoClick}>
          {logoContent}
        </Link>
      ) : (
        logoContent
      )}
    </div>
  );
});

MondelezLogo.displayName = "MondelezLogo";

export default MondelezLogo;
